import React, { useState, useEffect } from 'react';
import { Building, Plus, Users, Shield, Loader2, X, Search, CheckCircle, AlertCircle } from 'lucide-react';
import { api } from '../utils/api';
import { formatDateThai } from '../utils/helpers';

const SuperAdminDashboard = ({ user }) => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // State สำหรับ Modal เพิ่มโรงเรียน
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [formData, setFormData] = useState({
    org_name: '',
    admin_national_id: '',
    admin_first_name: '',
    admin_last_name: '',
    admin_employee_id: ''
  });

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const data = await api.getSuperAdminOrganizations();
      setOrganizations(data || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.createOrganizationWithAdmin(formData);
      if (response.success) {
        setMessage({ type: 'success', text: 'สร้างโรงเรียนและบัญชีผู้ดูแลระบบสำเร็จ!' });
        fetchOrganizations();
        // ล้างฟอร์ม
        setFormData({
          org_name: '', admin_national_id: '', admin_first_name: '', admin_last_name: '', admin_employee_id: ''
        });
        setTimeout(() => setShowModal(false), 2000);
      } else {
        setMessage({ type: 'error', text: response.error || 'เกิดข้อผิดพลาด' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อ' });
    } finally {
      setSubmitting(false);
    }
  };

  const filteredOrgs = organizations.filter(org => 
    org.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Shield className="h-8 w-8 mr-3 text-purple-600" />
            ระบบจัดการระดับสูง (Super Admin)
          </h1>
          <p className="text-gray-600 mt-2">จัดการข้อมูลโรงเรียนและผู้ดูแลระบบในเครือข่ายทั้งหมด</p>
        </div>
        <button
          onClick={() => { setShowModal(true); setMessage({type:'', text:''}); }}
          className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center transition-colors shadow-sm"
        >
          <Plus className="h-5 w-5 mr-2" />
          สร้างโรงเรียนใหม่
        </button>
      </div>

      {/* สถิติภาพรวม */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 flex items-center space-x-4">
          <div className="bg-blue-100 p-3 rounded-lg"><Building className="h-8 w-8 text-blue-600" /></div>
          <div>
            <p className="text-sm text-gray-500 font-medium">จำนวนโรงเรียนทั้งหมด</p>
            <p className="text-2xl font-bold text-gray-900">{organizations.length} แห่ง</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 flex items-center space-x-4">
          <div className="bg-green-100 p-3 rounded-lg"><Users className="h-8 w-8 text-green-600" /></div>
          <div>
            <p className="text-sm text-gray-500 font-medium">จำนวนบุคลากรรวม</p>
            <p className="text-2xl font-bold text-gray-900">
              {organizations.reduce((sum, org) => sum + parseInt(org.total_employees || 0), 0)} คน
            </p>
          </div>
        </div>
      </div>

      {/* ตารางแสดงรายชื่อโรงเรียน */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row justify-between items-center gap-4">
          <h2 className="text-lg font-bold text-gray-800">รายชื่อโรงเรียนในระบบ</h2>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input 
              type="text" placeholder="ค้นหาชื่อโรงเรียน..." 
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500 text-sm"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex justify-center items-center p-12">
              <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-white">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ชื่อโรงเรียน</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ผู้ดูแลระบบ (Admin)</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">จำนวนบุคลากร</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">วันที่เข้าร่วม</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrgs.length > 0 ? filteredOrgs.map((org) => (
                  <tr key={org.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">#{org.id}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{org.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {org.admin_name ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {org.admin_name}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center font-semibold text-blue-600">
                      {org.total_employees}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDateThai(org.created_at)}
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">ไม่พบข้อมูลโรงเรียน</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modal สำหรับเพิ่มโรงเรียนและแอดมิน */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-gray-50">
              <h3 className="text-xl font-bold text-gray-900 flex items-center">
                <Building className="h-6 w-6 mr-2 text-purple-600" />
                สร้างโรงเรียนและแอดมินใหม่
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-red-500 transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              {message.text && (
                <div className={`mb-6 p-4 rounded-lg flex items-center ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                  {message.type === 'success' ? <CheckCircle className="h-5 w-5 mr-2" /> : <AlertCircle className="h-5 w-5 mr-2" />}
                  {message.text}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* ข้อมูลโรงเรียน */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 border-b pb-2">1. ข้อมูลโรงเรียน</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ชื่อโรงเรียน (เต็ม) *</label>
                    <input
                      type="text" required
                      value={formData.org_name}
                      onChange={e => setFormData({...formData, org_name: e.target.value})}
                      className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="เช่น โรงเรียนสาธิตมหาวิทยาลัย..."
                    />
                  </div>
                </div>

                {/* ข้อมูลแอดมิน */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 border-b pb-2">2. ข้อมูลผู้ดูแลระบบ (Admin) คนแรก</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">รหัสบัตรประชาชน (ใช้ Login) *</label>
                      <input
                        type="text" required maxLength="13"
                        value={formData.admin_national_id}
                        onChange={e => setFormData({...formData, admin_national_id: e.target.value.replace(/[^0-9]/g, '')})}
                        className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="เลข 13 หลัก"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">รหัสพนักงาน (เว้นว่างเพื่อสุ่ม)</label>
                      <input
                        type="text"
                        value={formData.admin_employee_id}
                        onChange={e => setFormData({...formData, admin_employee_id: e.target.value})}
                        className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="เช่น ADM001"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">ชื่อ *</label>
                      <input
                        type="text" required
                        value={formData.admin_first_name}
                        onChange={e => setFormData({...formData, admin_first_name: e.target.value})}
                        className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">นามสกุล</label>
                      <input
                        type="text"
                        value={formData.admin_last_name}
                        onChange={e => setFormData({...formData, admin_last_name: e.target.value})}
                        className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-4 flex justify-end space-x-3">
                  <button
                    type="button" onClick={() => setShowModal(false)}
                    className="px-5 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                  >
                    ยกเลิก
                  </button>
                  <button
                    type="submit" disabled={submitting}
                    className="px-5 py-2.5 text-white bg-purple-600 hover:bg-purple-700 rounded-lg font-medium flex items-center transition-colors shadow-sm disabled:opacity-50"
                  >
                    {submitting ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Plus className="h-5 w-5 mr-2" />}
                    สร้างข้อมูล
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;